import { useCallback, useMemo, useState } from 'react';
import cl from 'classnames';
import filter from 'lodash/filter';
import get from 'lodash/get';
import size from 'lodash/size';
import take from 'lodash/take';
import sortBy from 'lodash/sortBy';

import {
  ChangeProductsFiltersFunc,
  FetchProductsFilterFields,
  FetchProductsFilters
} from '../../../../../../products/productsTypes';
import { CacheFetchProductsAggregationsQueryResponseAggregations } from '../../../../../../products/queries/fetchCacheProductsAggregations.query';
import { IconsEnum } from '../../../../../../../assets/icons/types';

import { ThreeDStockProductsFiltersAiTagsFilterItem } from './components/ThreeDStockProductsFiltersAiTagsFilterItem';

import { ButtonHelper } from '../../../../../../../helpers/buttons/ButtonHelper';
import { Icon } from '../../../../../../../helpers/Icon';
import { Translate } from '../../../../../../../helpers/Translate';
import { PureIconButtonHelper } from '../../../../../../../helpers/buttons/PureIconButtonHelper';

import { categoriesKeys, words } from '../../../../../../../locales/keys';

const enum productsAggregationsKeys {
  AI_FEATURE_TAGS = 'ai_feature_tags',
  AI_INTERIOR_STYLE_TAGS = 'ai_interior_style_tags',
  AI_MATERIAL_TAGS = 'ai_material_tags',
  AI_ROOM_CLASSIFICATION_TAGS = 'ai_room_classification_tags'
}

const aiTagsNames = {
  [FetchProductsFilterFields.AI_FEATURE_TAGS]:
    productsAggregationsKeys.AI_FEATURE_TAGS,
  [FetchProductsFilterFields.AI_INTERIOR_STYLE_TAGS]:
    productsAggregationsKeys.AI_INTERIOR_STYLE_TAGS,
  [FetchProductsFilterFields.AI_MATERIAL_TAGS]:
    productsAggregationsKeys.AI_MATERIAL_TAGS,
  [FetchProductsFilterFields.AI_ROOM_CLASSIFICATION_TAGS]:
    productsAggregationsKeys.AI_ROOM_CLASSIFICATION_TAGS
};

function getTags(
  productsAggregations: CacheFetchProductsAggregationsQueryResponseAggregations,
  fieldName: FetchProductsFilterFields
) {
  const tagsKey = aiTagsNames[fieldName] as productsAggregationsKeys;

  return sortBy(get(productsAggregations, [tagsKey, 'en']), 'name');
}

const aiTagsTitles = {
  [FetchProductsFilterFields.AI_FEATURE_TAGS]: categoriesKeys.feature,
  [FetchProductsFilterFields.AI_INTERIOR_STYLE_TAGS]:
    categoriesKeys.styles.singular,
  [FetchProductsFilterFields.AI_MATERIAL_TAGS]:
    categoriesKeys.materials.singular,
  [FetchProductsFilterFields.AI_ROOM_CLASSIFICATION_TAGS]: categoriesKeys.room,
  [FetchProductsFilterFields.BRAND_ID]: categoriesKeys.brands
};

const INITIAL_TAGS_COUNT = 6;

interface ThreeDStockProductsFiltersAiTagsFilterProps {
  productsAggregations: CacheFetchProductsAggregationsQueryResponseAggregations;
  fieldName: FetchProductsFilterFields;
  productsFilters: FetchProductsFilters;
  changeProductsFilters: ChangeProductsFiltersFunc;
  isCompact?: boolean;
}

function ThreeDStockProductsFiltersAiTagsFilter({
  productsAggregations,
  changeProductsFilters,
  fieldName,
  productsFilters,
  isCompact
}: ThreeDStockProductsFiltersAiTagsFilterProps) {
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [minimized, setMinimized] = useState<boolean>(true);

  const toggleIsOpen = useCallback<() => void>(
    () => setIsOpen((prevState) => !prevState),
    []
  );

  const toggleMinimize = useCallback<() => void>(() => {
    setMinimized((prevState) => !prevState);
  }, [setMinimized]);

  const tags = getTags(productsAggregations, fieldName);
  const checkedTags: string[] = useMemo(
    () => productsFilters[fieldName]?.in || [],
    [productsFilters, fieldName]
  );

  const handleCheckedTag = useCallback(
    (tagName: string, checked: boolean) => {
      const newTags = checked
        ? [...checkedTags, tagName]
        : filter(checkedTags, (tag) => tag !== tagName);

      changeProductsFilters(
        {
          [fieldName]: {
            in: newTags
          }
        },
        size(newTags) > 0 ? [] : [fieldName]
      );
    },
    [checkedTags, changeProductsFilters, fieldName]
  );

  if (size(tags) === 0) {
    return null;
  }

  return (
    <div className={cl(isCompact ? 'p-2' : 'p-4')}>
      <div className={cl('flex flex-col', isCompact ? 'gap-1.5' : 'gap-3')}>
        <ButtonHelper
          className="flex items-center gap-2 rounded-sm"
          dataGa={`${fieldName}-ai_tags_filter`}
          onClick={toggleIsOpen}
        >
          <span
            className={cl(
              'font-semibold text-left flex-1',
              isCompact ? 'text-xs' : 'text-sm'
            )}
          >
            <Translate id={aiTagsTitles[fieldName]} />
          </span>

          <Icon
            icon={isOpen ? IconsEnum.CHEVRON_DOWN_SOLID : IconsEnum.CHEVRON_UP}
            className={cl('text-gray-500', isCompact ? 'h-4 w-4' : 'h-5 w-5')}
          />
        </ButtonHelper>

        {isOpen && (
          <ul>
            {(minimized ? take(tags, INITIAL_TAGS_COUNT) : tags)?.map(
              (tag, index) => (
                <ThreeDStockProductsFiltersAiTagsFilterItem
                  key={index}
                  checked={checkedTags?.includes(tag.name)}
                  onCheckedTag={handleCheckedTag}
                  tag={tag}
                  isCompact={isCompact}
                />
              )
            )}

            {size(tags) > INITIAL_TAGS_COUNT && (
              <PureIconButtonHelper
                className="p-0 space-x-1 flex gap-1 text-gray-900 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-100 leading-5 focus:ring-base text-current hover:underline transition-colors duration-150 ease-out"
                i18nText={minimized ? words.showAll : words.showLess}
                i18nTextClassName={cl(
                  isCompact ? 'text-xs' : 'text-sm',
                  'underline'
                )}
                onClick={toggleMinimize}
                icon={
                  minimized
                    ? IconsEnum.CHEVRON_DOWN_SOLID
                    : IconsEnum.CHEVRON_UP
                }
                iconClassName={isCompact ? 'w-4 h-4' : 'w-5 h-5'}
              />
            )}
          </ul>
        )}
      </div>
    </div>
  );
}

export default ThreeDStockProductsFiltersAiTagsFilter;
